import React, { useState, useEffect } from 'react';
import { useFinance } from '../context/FinanceContext';
import { X, Sparkles, Check, DollarSign, Wallet, ShieldCheck, RefreshCw, Calculator, ArrowRight } from 'lucide-react';

interface LifestyleExpensesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LifestyleExpensesModal: React.FC<LifestyleExpensesModalProps> = ({ isOpen, onClose }) => {
  const { 
    currency, 
    convertToDisplay, 
    convertFromInput, 
    budgets,
    lifestyleMonthlyExpenses, 
    setLifestyleMonthlyExpenses, 
    financialFreedomStats,
    isDarkMode 
  } = useFinance();

  const [inputVal, setInputVal] = useState<string>('');
  const [useBudget, setUseBudget] = useState<boolean>(true);

  useEffect(() => {
    if (isOpen) {
      if (lifestyleMonthlyExpenses !== null) {
        setUseBudget(false);
        setInputVal(Math.round(convertToDisplay(lifestyleMonthlyExpenses)).toString());
      } else {
        setUseBudget(true);
        const budgetTotal = budgets.reduce((s, b) => s + (b.budget || 0), 0);
        setInputVal(Math.round(convertToDisplay(budgetTotal > 0 ? budgetTotal : 3500)).toString());
      }
    }
  }, [isOpen, lifestyleMonthlyExpenses, budgets, convertToDisplay]);

  if (!isOpen) return null;

  const currentDisplayMonthly = parseFloat(inputVal) || 0;
  const currentDisplayAnnual = currentDisplayMonthly * 12;

  // Live simulation of Freedom stats based on user modal input
  const currentAnnualPassiveDisplay = convertToDisplay(financialFreedomStats.totalAnnualPassive);
  const currentAnnual4PctDisplay = convertToDisplay(financialFreedomStats.annualWithdrawal4Pct);
  const currentTotalFreedomIncomeDisplay = currentAnnualPassiveDisplay + currentAnnual4PctDisplay;
  
  const simulatedPercentage = currentDisplayAnnual > 0 
    ? Math.min(100, Math.max(0, (currentTotalFreedomIncomeDisplay / currentDisplayAnnual) * 100))
    : 0;

  const simulatedShortfallAnnual = Math.max(0, currentDisplayAnnual - currentTotalFreedomIncomeDisplay);
  const simulatedCapitalNeeded = simulatedShortfallAnnual > 0 ? simulatedShortfallAnnual / 0.04 : 0;

  const budgetMonthlyDisplay = Math.round(convertToDisplay(financialFreedomStats.budgetDefaultMonthly > 0 ? financialFreedomStats.budgetDefaultMonthly : 3500));

  const handleSelectBudget = () => {
    setUseBudget(true);
    setInputVal(budgetMonthlyDisplay.toString());
  };

  const handleApplyPreset = (multiplier: number) => {
    setUseBudget(false);
    const newVal = Math.round(budgetMonthlyDisplay * multiplier);
    setInputVal(newVal.toString());
  };

  const handleSave = () => {
    if (useBudget) {
      setLifestyleMonthlyExpenses(null); // Resets to automatically track budgeted monthly expenses
    } else {
      const num = parseFloat(inputVal) || 0;
      setLifestyleMonthlyExpenses(convertFromInput(num));
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2.5 bg-teal-500/20 text-teal-300 rounded-2xl border border-teal-500/30">
              <Calculator size={20} />
            </span>
            <div>
              <h3 className="text-lg font-black tracking-tight text-white">Target Lifestyle Expenses</h3>
              <p className="text-xs text-slate-300 font-medium">Financial Freedom via Passive Income + 4% Rule</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 overflow-y-auto">
          {/* Default Option: Budgeted Monthly Expenses */}
          <div 
            onClick={handleSelectBudget}
            className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-center justify-between ${
              useBudget 
                ? 'bg-teal-50/70 border-teal-500/80 shadow-sm' 
                : 'bg-slate-50 border-slate-200/80 hover:bg-slate-100/60'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                useBudget ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300 bg-white'
              }`}>
                {useBudget && <Check size={12} strokeWidth={3} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-slate-900 uppercase tracking-wider">
                    Dynamic Budget Default
                  </span>
                  <span className="text-[9.5px] font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">
                    Recommended
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  Synchronizes automatically with your budgeted monthly expenses
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-base font-black text-teal-700">
                {currency} {budgetMonthlyDisplay.toLocaleString()}
              </div>
              <span className="text-[10px] font-bold text-slate-400">/month</span>
            </div>
          </div>

          {/* Custom Lifestyle Option */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black text-slate-700 uppercase tracking-wider">
                Custom Lifestyle Monthly Target
              </label>
              {!useBudget && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  Custom Active
                </span>
              )}
            </div>

            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-black text-slate-400">
                {currency}
              </span>
              <input 
                type="number"
                step="50"
                value={inputVal}
                onChange={e => {
                  setUseBudget(false);
                  setInputVal(e.target.value);
                }}
                placeholder="Enter monthly target"
                className="w-full pl-10 pr-16 py-3.5 bg-white rounded-2xl text-base font-black text-slate-900 border border-slate-200 outline-none focus:border-teal-500 shadow-sm"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                / month
              </span>
            </div>

            {/* Quick Presets based on budget */}
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">Presets:</span>
              <button 
                type="button"
                onClick={() => handleApplyPreset(0.8)}
                className="px-2.5 py-1 text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                Lean FIRE (-20%)
              </button>
              <button 
                type="button"
                onClick={() => handleApplyPreset(1.0)}
                className="px-2.5 py-1 text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                100% Budget
              </button>
              <button 
                type="button"
                onClick={() => handleApplyPreset(1.25)}
                className="px-2.5 py-1 text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                Comfort (+25%)
              </button>
              <button 
                type="button"
                onClick={() => handleApplyPreset(1.5)}
                className="px-2.5 py-1 text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                Fat FIRE (+50%)
              </button>
            </div>
          </div>

          {/* Live Freedom Simulation Card */}
          <div className="p-5 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl space-y-4 shadow-md">
            <div className="flex items-center justify-between pb-3 border-b border-slate-700/70">
              <span className="text-[11px] font-black uppercase tracking-wider text-teal-400">
                Freedom Simulation Live
              </span>
              <span className="text-xs font-black text-white px-2.5 py-0.5 rounded-full bg-slate-700">
                {simulatedPercentage.toFixed(1)}% Freedom
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Target Annual Lifestyle</span>
                <span className="text-sm font-black text-white mt-1 block">
                  {currency} {Math.round(currentDisplayAnnual).toLocaleString()}
                </span>
                <span className="text-[9.5px] text-slate-400">({currency} {Math.round(currentDisplayMonthly).toLocaleString()}/mo)</span>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Current Freedom Cash Flow</span>
                <span className="text-sm font-black text-teal-400 mt-1 block">
                  +{currency} {Math.round(currentTotalFreedomIncomeDisplay).toLocaleString()}/yr
                </span>
                <span className="text-[9.5px] text-teal-300/80">Passive + 4% Rule</span>
              </div>
            </div>

            {/* Need to invest to complete */}
            <div className="p-3.5 bg-teal-950/60 rounded-xl border border-teal-500/40 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-teal-300 block">
                  Capital Needed to Invest to Complete
                </span>
                <span className="text-[11px] text-slate-300 mt-0.5 block">
                  At standard 4% withdrawal rate (25x shortfall)
                </span>
              </div>
              <div className="text-right">
                <span className="text-base font-black text-teal-300">
                  {currency} {Math.round(simulatedCapitalNeeded).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-3">
          <button 
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-200 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button 
            type="button"
            onClick={handleSave}
            className="px-6 py-2.5 text-xs font-black uppercase tracking-wider bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-md transition-all active:scale-95 cursor-pointer flex items-center gap-2"
          >
            <Check size={14} /> Apply Lifestyle Target
          </button>
        </div>
      </div>
    </div>
  );
};
